import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import AppLayout from './components/layout/AppLayout';
import LandingPage from './pages/LandingPage';
import UseCasePage from './pages/UseCasePage';
import BlogPage from './pages/BlogPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import TransactionsPage from './pages/TransactionsPage';
import AccountsPage from './pages/AccountsPage';
import JournalPage from './pages/JournalPage';
import JournalEntryFormPage from './pages/JournalEntryFormPage';
import InvoicesPage from './pages/InvoicesPage';
import CustomersPage from './pages/CustomersPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import PlatformAdminPage from './pages/PlatformAdminPage';
import GTMAdminPage from './pages/GTMAdminPage';
import AIChatbot from './components/AIChatbot';
import './App.css';

const PUBLIC_PATHS = ['/', '/login', '/terms', '/privacy', '/onboarding'];

function ChatbotWrapper() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  if (!isAuthenticated || PUBLIC_PATHS.includes(location.pathname)) {
    return null;
  }
  
  return <AIChatbot />;
}

function LandingRedirect() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Detect session_id synchronously during render (not in useEffect).
  // This prevents race conditions with ProtectedRoute and AuthProvider.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/for/:persona" element={<UseCasePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/gtm-admin" element={<GTMAdminPage />} />
        
        {/* Protected routes */}
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/journal" element={<JournalPage />} />
          <Route path="/journal/new" element={<JournalEntryFormPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/account" element={<SettingsPage />} />
          <Route path="/platform-admin" element={<PlatformAdminPage />} />
        </Route>
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ChatbotWrapper />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </OrgProvider>
    </AuthProvider>
  );
}

export default App;
